import { FLAGS } from '@iheartjane/feature-flags';
import { useToast } from '@iheartjane/reefer';
import type { LoaderFunctionArgs, MetaFunction } from 'react-router';
import { useLoaderData } from 'react-router';

import { canonicalTag } from '@jane/bloom/resources/meta/canonicalTag';
import { description } from '@jane/bloom/resources/meta/description';
import { getRootLoaderData } from '@jane/bloom/resources/meta/getRootLoaderData';
import { parentMeta } from '@jane/bloom/resources/meta/parentMeta';
import { title } from '@jane/bloom/resources/meta/title';
import { getEmailSession } from '@jane/bloom/sessions/email.server';
import type { RouteHandle } from '@jane/bloom/types/schemas/bloom/routeHandle';
import { ensureBloomMenu } from '@jane/bloom/utils/ensureBloomMenu.server';
import { isFeatureFlagsEnabled } from '@jane/bloom/utils/isFeatureFlagEnabled.server';

import { Welcome } from './components/welcome';

export const handle: RouteHandle = {
  hydrate: true,
};

export const meta: MetaFunction<typeof loader> = (args) => {
  const {
    menuUrl,
    operator: { name },
    pinnedStore,
  } = getRootLoaderData(args);
  return [
    title('Sign in', pinnedStore?.name || name),
    description(),
    canonicalTag(args.location, menuUrl),
    ...parentMeta(args),
  ];
};

export async function loader({ params, request }: LoaderFunctionArgs) {
  const { cookie_prefix, google_sso_client_id: googleSsoClientId } =
    await ensureBloomMenu(request);

  const { getSession } = getEmailSession(cookie_prefix);
  const emailSession = await getSession(request.headers.get('Cookie'));
  const userEmail = emailSession.get('email')?.email;

  const isAfterReset = params['reset'];

  const featureFlags = await isFeatureFlagsEnabled({
    flagNames: [FLAGS.bloomMessagingForMigratedUsers],
    request,
  });

  const isBloomMessagingForMigratedUsersEnabled =
    featureFlags[FLAGS.bloomMessagingForMigratedUsers];

  return {
    googleSsoClientId,
    isAfterReset,
    isBloomMessagingForMigratedUsersEnabled,
    userEmail,
  };
}

export default function LoginRoute() {
  const { isAfterReset, isBloomMessagingForMigratedUsersEnabled, userEmail } =
    useLoaderData<typeof loader>();
  const toast = useToast();

  if (isAfterReset) {
    toast.add({
      label: 'Check your email for a link to reset your password from Jane.',
      variant: 'success',
    });
  }

  return (
    <Welcome
      userEmail={userEmail}
      isBloomMessagingForMigratedUsersEnabled={
        !!isBloomMessagingForMigratedUsersEnabled
      }
    />
  );
}
